import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IChapterGivingAccount } from "@dc-ireland/shared"
import AdminAlerts from "../../components/admin/AdminAlerts"

const emptyForm = {
  chapterSlug: "",
  label: "",
  accountName: "",
  bankName: "",
  accountNumber: "",
  iban: "",
  bic: "",
  reference: "",
  paypalUrl: "",
  sumupUrl: "",
  sortOrder: "0",
  enabled: true,
}

const AdminGiving = () => {
  const [accounts, setAccounts] = useState<IChapterGivingAccount[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadAccounts = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiClient.getGivingAccounts(true)
      setAccounts(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load giving accounts")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAccounts()
  }, [])

  const resetForm = () => {
    setForm(emptyForm)
    setEditingSlug(null)
  }

  const startEdit = (account: IChapterGivingAccount) => {
    setEditingSlug(account.chapterSlug)
    setForm({
      chapterSlug: account.chapterSlug,
      label: account.label,
      accountName: account.accountName,
      bankName: account.bankName ?? "",
      accountNumber: account.accountNumber ?? "",
      iban: account.iban ?? "",
      bic: account.bic ?? "",
      reference: account.reference ?? "",
      paypalUrl: account.paypalUrl ?? "",
      sumupUrl: account.sumupUrl ?? "",
      sortOrder: String(account.sortOrder ?? 0),
      enabled: account.enabled !== false,
    })
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    const payload: Omit<IChapterGivingAccount, "id"> = {
      chapterSlug: form.chapterSlug.trim(),
      label: form.label.trim(),
      accountName: form.accountName.trim(),
      bankName: form.bankName.trim() || undefined,
      accountNumber: form.accountNumber.trim() || undefined,
      iban: form.iban.trim() || undefined,
      bic: form.bic.trim() || undefined,
      reference: form.reference.trim() || undefined,
      paypalUrl: form.paypalUrl.trim() || undefined,
      sumupUrl: form.sumupUrl.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 0,
      enabled: form.enabled,
    }

    try {
      if (editingSlug) {
        await apiClient.updateGivingAccount(editingSlug, payload)
        setSuccess("Giving account updated.")
      } else {
        await apiClient.createGivingAccount(payload)
        setSuccess("Giving account created.")
      }
      resetForm()
      await loadAccounts()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save giving account")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (chapterSlug: string) => {
    if (!confirm(`Delete giving details for "${chapterSlug}"?`)) return
    try {
      setError(null)
      await apiClient.deleteGivingAccount(chapterSlug)
      setSuccess("Giving account deleted.")
      if (editingSlug === chapterSlug) resetForm()
      await loadAccounts()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete giving account")
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">Chapter Giving</h2>
        <p className="mt-1 text-secondary">
          Bank transfer, PayPal, and SumUp details shown when someone opens a chapter on the Give page.
        </p>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          <h3 className="card-title">
            {editingSlug ? `Edit ${editingSlug}` : "Add chapter giving account"}
          </h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="form-control">
              <span className="label-text">Chapter slug *</span>
              <input
                className="input input-bordered w-full"
                required
                disabled={Boolean(editingSlug)}
                value={form.chapterSlug}
                onChange={(e) => setForm({ ...form, chapterSlug: e.target.value })}
                placeholder="dublin"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Label *</span>
              <input
                className="input input-bordered w-full"
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Dublin"
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Account name *</span>
              <input
                className="input input-bordered w-full"
                required
                value={form.accountName}
                onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                placeholder="Dominion City Dublin"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Bank name</span>
              <input
                className="input input-bordered w-full"
                value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                placeholder="Bank of Ireland"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Account number</span>
              <input
                className="input input-bordered w-full"
                value={form.accountNumber}
                onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">IBAN</span>
              <input
                className="input input-bordered w-full"
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="IE00…"
              />
            </label>
            <label className="form-control">
              <span className="label-text">BIC / SWIFT</span>
              <input
                className="input input-bordered w-full"
                value={form.bic}
                onChange={(e) => setForm({ ...form, bic: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Payment reference</span>
              <input
                className="input input-bordered w-full"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="Dublin Donation"
              />
            </label>
            <label className="form-control">
              <span className="label-text">PayPal URL</span>
              <input
                className="input input-bordered w-full"
                value={form.paypalUrl}
                onChange={(e) => setForm({ ...form, paypalUrl: e.target.value })}
                placeholder="https://paypal.me/…"
              />
            </label>
            <label className="form-control">
              <span className="label-text">SumUp URL</span>
              <input
                className="input input-bordered w-full"
                value={form.sumupUrl}
                onChange={(e) => setForm({ ...form, sumupUrl: e.target.value })}
                placeholder="https://pay.sumup.com/…"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Sort order</span>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </label>
            <label className="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                className="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              <span className="label-text">Enabled on public Give page</span>
            </label>
          </div>

          <div className="card-actions justify-end gap-2">
            {editingSlug && (
              <button type="button" className="btn btn-ghost" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : editingSlug ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </form>

      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h3 className="card-title">Accounts</h3>
          {loading ? (
            <p className="text-secondary">Loading…</p>
          ) : accounts.length === 0 ? (
            <p className="text-secondary">No giving accounts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Chapter</th>
                    <th>Account</th>
                    <th>PayPal</th>
                    <th>SumUp</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.chapterSlug}>
                      <td>
                        <div className="font-medium">{account.label}</div>
                        <div className="text-xs text-secondary">{account.chapterSlug}</div>
                      </td>
                      <td className="max-w-xs truncate text-sm">
                        {account.iban || account.accountNumber || account.bankName || "—"}
                      </td>
                      <td className="max-w-[10rem] truncate text-sm">
                        {account.paypalUrl ? "Yes" : "—"}
                      </td>
                      <td className="max-w-[10rem] truncate text-sm">
                        {account.sumupUrl ? "Yes" : "—"}
                      </td>
                      <td>{account.enabled === false ? "Disabled" : "Enabled"}</td>
                      <td className="space-x-2 text-right">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => startEdit(account)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-error"
                          onClick={() => void handleDelete(account.chapterSlug)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminGiving
